import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Modal,
  Typography,
  TextField,
  LinearProgress,
} from "@mui/material";
import XLSX from "xlsx-js-style";
import {
  DepartmentSelect,
  FilialSelect,
  FilialDivisionSelect2,
} from "../../../components/Select/domainSelects";
import { deviceIndicatorCRUD } from "../../../http";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const toDateOrNull = (v) => {
  if (v == null || v === "") return null;
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v;
  if (typeof v === "number") {
    // excel serial date
    const parsed = XLSX.SSF?.parse_date_code?.(v);
    if (parsed && parsed.y && parsed.m && parsed.d) {
      return new Date(parsed.y, parsed.m - 1, parsed.d);
    }
  }
  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    // dd.mm.yyyy
    const m = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (m) {
      const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const toIntOrDefault = (v, def = 1) => {
  if (v == null || v === "") return def;
  const n = Number(v);
  if (!Number.isFinite(n)) return def;
  return Math.max(1, Math.trunc(n));
};

// Try to detect header row and map columns by name. If not found, use fixed indexes.
const detectColumnMap = (aoa) => {
  const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const wanted = [
    { key: "name", variants: ["наименование"] },
    { key: "type", variants: ["тип"] },
    { key: "serialNumber", variants: ["заводской номер", "заводскои номер", "серийный номер", "серийныи номер"] },
    { key: "manufactureYear", variants: ["год выпуска", "год"] },
    { key: "quantity", variants: ["кол-во", "количество", "к о л в о"] },
    { key: "rankAccuracyError", variants: ["разряд, класс точности, погрешность", "разряд", "класс точности", "погрешность"] },
    { key: "measurementRange", variants: ["диапазон измерения", "диапазон"] },
    { key: "lastVerificationDate", variants: ["последней поверки", "дата последней поверки", "последняя поверка"] },
    { key: "nextVerificationDate", variants: ["последующей поверки", "дата последующей поверки", "следующая поверка"] },
    { key: "note", variants: ["примечание"] },
    { key: "periodicity", variants: ["периодичность"] },
  ];

  for (let r = 0; r < Math.min(aoa.length, 30); r++) {
    const row = aoa[r] || [];
    const normalizedCells = row.map(norm);
    const found = {};
    wanted.forEach((w) => {
      const idx = normalizedCells.findIndex((c) => w.variants.some((v) => c.includes(norm(v))));
      if (idx >= 0) found[w.key] = idx;
    });
    // if we at least found name and dates or note, accept
    if (found.name != null) {
      return { headerRow: r, map: found };
    }
  }

  // fallback to fixed column positions (as in screenshot/table):
  // 0: №, 1: name, 2: type, 3: serial, 4: year, 5: qty, 6: rank, 7: range, 8: last, 9: next, 10: note, 11: periodicity
  return {
    headerRow: 0,
    map: {
      name: 1,
      type: 2,
      serialNumber: 3,
      manufactureYear: 4,
      quantity: 5,
      rankAccuracyError: 6,
      measurementRange: 7,
      lastVerificationDate: 8,
      nextVerificationDate: 9,
      note: 10,
      periodicity: 11,
    },
  };
};

const pick = (row, idx) => {
  if (idx == null) return "";
  return row?.[idx] ?? "";
};

export default function EquipmentIndicatorImportExcelModal({
  open,
  onClose,
  onDone,
}) {
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [parseInfo, setParseInfo] = useState(null);

  const [filialId, setFilialId] = useState("");
  const [filialDivisionId, setFilialDivisionId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [periodicity, setPeriodicity] = useState("");

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errors, setErrors] = useState([]);

  const preview = useMemo(() => rows.slice(0, 5), [rows]);

  const reset = () => {
    setFileName("");
    setRows([]);
    setParseInfo(null);
    setErrors([]);
    setUploading(false);
    setProgress({ done: 0, total: 0 });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setErrors([]);

    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array", cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });

    const detected = detectColumnMap(aoa);
    setParseInfo(detected);

    const startRow = detected.headerRow + 1;
    const parsed = [];

    for (let r = startRow; r < aoa.length; r++) {
      const row = aoa[r];
      if (!row) continue;
      // consider empty if name and type and serial are empty
      const name = String(pick(row, detected.map.name) || "").trim();
      const type = String(pick(row, detected.map.type) || "").trim();
      const serial = String(pick(row, detected.map.serialNumber) || "").trim();
      if (!name && !type && !serial) continue;

      const item = {
        __row: r + 1, // 1-based excel row index
        name,
        type: String(pick(row, detected.map.type) || "").trim() || null,
        serialNumber: String(pick(row, detected.map.serialNumber) || "").trim() || null,
        manufactureYear: String(pick(row, detected.map.manufactureYear) || "").trim() || null,
        quantity: toIntOrDefault(pick(row, detected.map.quantity), 1),
        rankAccuracyError: String(pick(row, detected.map.rankAccuracyError) || "").trim() || null,
        measurementRange: String(pick(row, detected.map.measurementRange) || "").trim() || null,
        note: String(pick(row, detected.map.note) || "").trim() || null,
        periodicity: String(pick(row, detected.map.periodicity) || "").trim() || null,
        lastVerificationDate: toDateOrNull(pick(row, detected.map.lastVerificationDate)),
        nextVerificationDate: toDateOrNull(pick(row, detected.map.nextVerificationDate)),
      };

      // If user selected periodicity in modal, it overrides file
      if (periodicity) item.periodicity = periodicity;

      parsed.push(item);
    }

    setRows(parsed);
  };

  const upload = async () => {
    const errs = [];

    if (!filialId) errs.push("Выберите филиал.");
    if (!departmentId) errs.push("Выберите лабораторию.");
    if (!rows.length) errs.push("Нет строк для импорта (проверьте Excel).");

    if (errs.length) {
      alert(errs.join("\n"));
      return;
    }

    setUploading(true);
    setErrors([]);
    setProgress({ done: 0, total: rows.length });

    const outErrors = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      try {
        if (!r.name) throw new Error("Пустое поле 'Наименование'");

        const payload = {
          name: r.name,
          type: r.type,
          serialNumber: r.serialNumber,
          manufactureYear: r.manufactureYear,
          quantity: r.quantity ?? 1,
          rankAccuracyError: r.rankAccuracyError,
          measurementRange: r.measurementRange,
          note: r.note,
          periodicity: r.periodicity,
          lastVerificationDate: r.lastVerificationDate,
          nextVerificationDate: r.nextVerificationDate,
          isIrrelevant: false,
          state: 1,
          filialId: Number(filialId),
          filialDivisionId: filialDivisionId ? Number(filialDivisionId) : null,
          departmentId: Number(departmentId),
        };

        await deviceIndicatorCRUD.create(payload);
      } catch (e) {
        outErrors.push({ row: r.__row, message: String(e) });
      } finally {
        setProgress((p) => ({ ...p, done: i + 1 }));
      }
    }

    setErrors(outErrors);
    setUploading(false);
    onDone && onDone({ total: rows.length, errors: outErrors.length });
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Импорт Excel — индикатор по поверке оборудования
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FilialSelect
              value={filialId}
              onChange={(id) => {
                setFilialId(id);
                setFilialDivisionId("");
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FilialDivisionSelect2
              filialId={filialId}
              value={filialDivisionId}
              onChange={(val) => setFilialDivisionId(val)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DepartmentSelect value={departmentId} onChange={setDepartmentId} />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Периодичность (опционально, применить ко всем строкам)"
              value={periodicity}
              onChange={(e) => setPeriodicity(e.target.value)}
              helperText={'Например: "раз в год" (если оставить пустым — берём из файла)'}
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="outlined" component="label" disabled={uploading}>
              Выбрать Excel
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleFile}
              />
            </Button>
            {fileName ? (
              <Typography sx={{ ml: 2, display: "inline-block" }}>
                {fileName}
              </Typography>
            ) : null}
          </Grid>

          {rows.length ? (
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }}>
                Найдено строк: <b>{rows.length}</b> (показано первые 5)
              </Typography>
              <Box
                sx={{
                  border: "1px solid #ddd",
                  borderRadius: 1,
                  p: 2,
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {preview
                  .map(
                    (r) =>
                      `#${r.__row} ${r.name} | ${r.type || ""} | ${r.serialNumber || ""} | qty=${r.quantity}`
                  )
                  .join("\n")}
              </Box>
            </Grid>
          ) : null}

          {uploading ? (
            <Grid item xs={12}>
              <Typography sx={{ mb: 1 }}>
                Загрузка: {progress.done}/{progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress.total ? (progress.done / progress.total) * 100 : 0}
              />
            </Grid>
          ) : null}

          {errors.length ? (
            <Grid item xs={12}>
              <Typography color="error" sx={{ mb: 1 }}>
                Ошибки: {errors.length}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #f2c2c2",
                  borderRadius: 1,
                  p: 2,
                  maxHeight: 200,
                  overflowY: "auto",
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {errors.map((e) => `Строка ${e.row}: ${e.message}`).join("\n")}
              </Box>
            </Grid>
          ) : null}

          <Grid item xs={12} sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button variant="contained" onClick={upload} disabled={uploading || !rows.length}>
              Загрузить в систему
            </Button>
            <Button variant="outlined" onClick={handleClose} disabled={uploading}>
              Закрыть
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}

